import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import {
  activityDisplayName,
  activityProgress,
  cardZone,
  isRestDay,
} from "../../helpers/activitySelectors";
import { Activity } from "../../model/workoutPlanTypes";
import WorkoutTypeIcon from "../shared/WorkoutTypeIcon";

type Props = {
  activity: Activity;
  /** Drawn with the primary border while its details are shown beside the week. */
  selected?: boolean;
  onOpen: (activity: Activity) => void;
};

const REST_DAY_HINT = "Take a well-deserved break!";

/**
 * One planned workout of a day, ported from the mobile `ActivityCard`: the
 * type glyph in a disc whose colour follows progress, the name, then one
 * line of calories against the target, duration and target zone. The card's
 * frame follows progress too — a green border once started, a green sheet
 * once completed. A Rest Day is the mobile's muted grey card with its hint.
 * Picking the card selects it for the details pane.
 */
const WorkoutCard = ({ activity, selected = false, onOpen }: Props) => {
  const theme: Theme = useTheme();
  const rest = isRestDay(activity);
  const progress = activityProgress(activity);
  const zone = cardZone(activity);
  const started = progress !== "not_started";

  // Mobile: "0/336 Kcal   60 min   Zone 2"; once started only the calories
  // stay, with the burned number in bold.
  const current = Math.round(activity.cals?.current ?? 0);
  const target = Math.round(activity.cals?.target ?? 0);
  const extras = rest
    ? []
    : [
        !started && activity.duration_in_minutes
          ? `${Math.round(activity.duration_in_minutes)} min`
          : "",
        !started && zone !== undefined ? `Zone ${zone}` : "",
      ].filter(Boolean);

  return (
    <Box
      sx={styles.card(theme, selected, rest, progress)}
      onClick={() => onOpen(activity)}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(activity);
        }
      }}
    >
      <Box sx={styles.titleRow}>
        <Box sx={styles.iconDisc(theme, progress, rest)}>
          <WorkoutTypeIcon
            type={activity.type}
            fill={
              rest
                ? theme.colors_ds.NEUTRAL_ICON_WEAK
                : progress === "in_progress"
                ? theme.colors_ds.NEUTRAL_TEXT_STRONG
                : theme.colors_ds.NEUTRAL_GENERAL_WHITE
            }
          />
        </Box>
        <Box sx={styles.title(theme, rest)} title={activityDisplayName(activity)}>
          {activityDisplayName(activity)}
        </Box>
      </Box>
      {rest ? (
        <Box sx={styles.restHint(theme)}>{REST_DAY_HINT}</Box>
      ) : (
        <Box sx={styles.metrics(theme, started)}>
          <Box component="span">
            <Box component="span" sx={styles.current(started)}>
              {current}
            </Box>
            /{target} Kcal
          </Box>
          {extras.map((metric) => (
            <Box key={metric} component="span">
              {metric}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default WorkoutCard;

const styles = {
  // Mobile frames: default white with a weak border; in progress keeps the
  // white sheet behind a green border; completed turns the sheet green; a
  // rest day sits on the muted grey sheet. Selection wins over all of them.
  card: (theme: Theme, selected: boolean, rest: boolean, progress: string) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    padding: "16px",
    borderRadius: "16px",
    border: `1px solid ${
      selected
        ? theme.colorTokens.P[60]
        : rest
        ? theme.colorTokens.N[40]
        : progress !== "not_started"
        ? theme.colorTokens.S[20]
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    backgroundColor: selected
      ? theme.colorTokens.P[10]
      : rest
      ? theme.colorTokens.N[40]
      : progress === "completed"
      ? theme.colorTokens.S[10]
      : theme.colors_ds.NEUTRAL_BG_MAIN,
    cursor: "pointer",
    transition: "border-color 0.2s, background-color 0.2s",
    "&:hover": { borderColor: theme.colorTokens.P[60] },
    "&:focus-visible": {
      outline: `2px solid ${theme.colorTokens.P[60]}`,
      outlineOffset: "2px",
    },
  }),
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
  },
  // Mobile: grey disc before a workout starts, elevated while in progress,
  // green once completed; the rest-day glyph sits on the elevated disc.
  iconDisc: (theme: Theme, progress: string, rest: boolean) => ({
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: "16px",
    backgroundColor: rest
      ? theme.colorTokens.N[30]
      : progress === "completed"
      ? theme.colorTokens.S[70]
      : progress === "in_progress"
      ? theme.colors_ds.NEUTRAL_BG_ELEVATION1
      : theme.colors_ds.NEUTRAL_ICON_WEAK,
  }),
  // One line, cut with an ellipsis; the full name is the hover title.
  title: (theme: Theme, rest: boolean) => ({
    flex: 1,
    minWidth: 0,
    color: rest ? theme.colors_ds.NEUTRAL_TEXT_WEAK : theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  // "0/336 Kcal   60 min   Zone 2"; stronger once the workout has started.
  metrics: (theme: Theme, started: boolean) => ({
    display: "flex",
    gap: "16px",
    color: started ? theme.colors_ds.NEUTRAL_TEXT_STRONG : theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  // The burned number alone goes bold once the workout has started (mobile).
  current: (started: boolean) => ({
    fontFamily: started ? "var(--font-Bold)" : "var(--font-Medium)",
  }),
  restHint: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
};
